<script setup>
import { ref } from 'vue'
import { resolveWidgetAsset } from '../lib/assets'

const failedImages = ref(new Set())

const props = defineProps({
  options: {
    type: Array,
    required: true,
  },
  modelValue: {
    type: Array,
    default: () => [],
  },
})
const emit = defineEmits(['update:modelValue'])

const isSelected = (id) => props.modelValue.includes(id)

const toggle = (id) => {
  const next = isSelected(id)
    ? props.modelValue.filter((entry) => entry !== id)
    : [...props.modelValue, id]

  emit('update:modelValue', next)
}
</script>

<template>
  <div class="checkbox-grid">
    <label
      v-for="option in props.options"
      :key="option.id"
      class="checkbox-card"
      :class="{ 'is-active': isSelected(option.id) }"
    >
      <input
        type="checkbox"
        :value="option.id"
        :checked="isSelected(option.id)"
        @change="toggle(option.id)"
      />
      <div class="checkbox-card__content">
        <img
          v-if="!failedImages.has(option.id)"
          class="checkbox-card__image"
          :src="resolveWidgetAsset(`images/${option.id}.webp`)"
          :alt="option.label"
          @error="failedImages = new Set([...failedImages, option.id])"
        />
        <svg
          v-else
          class="checkbox-card__image"
          viewBox="0 0 72 72"
          xmlns="http://www.w3.org/2000/svg"
        >
          <rect class="fallback-image__bg" x="1" y="1" width="70" height="70" />
          <text class="fallback-image__label" x="36" y="40" text-anchor="middle">
            {{ option.id }}
          </text>
        </svg>
      </div>
      <div class="checkbox-card__meta">
        <span class="checkbox-card__tag">{{ option.id }}</span>
        <span class="checkbox-card__check" aria-hidden="true"></span>
      </div>
    </label>
  </div>
</template>

<style scoped>
.checkbox-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, 72px);
  gap: var(--space-8);
  justify-content: center;
}

.checkbox-card {
  background: transparent;
  border-radius: 0.9rem;
  padding: 10px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  gap: 4px;
  font-size: 0.85rem;
  color: var(--semantic-text-muted);
  cursor: pointer;
  align-items: center;
  text-align: center;
}

.checkbox-card input {
  position: absolute;
  opacity: 0;
  pointer-events: none;
}

.checkbox-card__content {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.checkbox-card__image {
  width: 100%;
  aspect-ratio: 1;
  /* Type images vary in dimensions (square, tall, very wide); keep a square box and
     letterbox the image inside it instead of cropping. */
  object-fit: contain;
  background-color: var(--semantic-bg-default);
}

.checkbox-card__tag {
  font-size: 0.9rem;
  font-weight: 700;
  color: var(--semantic-text-default);
}

.checkbox-card__meta {
  display: flex;
  flex-direction: column;
  gap: 2px;
  align-items: center;
}

.checkbox-card__check {
  width: 16px;
  height: 16px;
  /* border-radius: 3px; */
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: var(--semantic-bg-petrol-strong);
}

.checkbox-card__check::after {
  content: '';
  width: 7px;
  height: 4px;
  border-left: 2px solid #ffffff;
  border-bottom: 2px solid #ffffff;
  transform: rotate(-45deg);
  opacity: 0;
}

.checkbox-card.is-active .checkbox-card__check::after {
  opacity: 1;
}
</style>
